import { ApiService } from '../ApiService';
import type { Account, CreateAccountPayload } from '../../../types/accounting';
import type { AccountSearchParams, BusinessUnitFilterBase } from '../../../types/filters';
import type { PaginatedResponse, ApiResponse } from '../types';

/**
 * Interface pour la réponse brute du backend (format différent du frontend)
 */
interface BackendAccountsResponse {
  accounts: Account[];
  total: number;
  page: number;
  perPage: number;
}

/**
 * Adaptateur pour normaliser la réponse du backend vers le format PaginatedResponse attendu
 * Backend: { accounts: [...], total, page, perPage }
 * Frontend: { data: [...], total, page, pageSize, totalPages }
 */
function normalizeAccountsResponse(backendData: BackendAccountsResponse): PaginatedResponse<Account> {
  const accounts = Array.isArray(backendData.accounts) ? backendData.accounts : [];
  const total = backendData.total || accounts.length;
  const pageSize = backendData.perPage || 20;
  const page = backendData.page || 1;
  const totalPages = Math.ceil(total / pageSize);
  
  return {
    data: accounts,
    total,
    page,
    pageSize,
    totalPages
  };
}

/**
 * Interface étendue pour la création de comptes conformément à la doc API
 * Tous les champs requis sont explicitement marqués
 */
export interface CreateAccountRequest extends BusinessUnitFilterBase {
  code: string;                    // Requis
  name: string;                    // Requis
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';  // Requis
  companyId: string;               // Requis
  fiscalYearId: string;            // Requis
  class?: string;
  standard?: 'SYSCOHADA' | 'IFRS';
  parentId?: string;
  isAnalytic?: boolean;
  description?: string;
  active?: boolean;
  metadata?: Record<string, any>;
}

/**
 * Interface pour la mise à jour d'un compte
 */
export interface UpdateAccountRequest {
  name?: string;
  type?: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  standard?: 'SYSCOHADA' | 'IFRS';
  parentId?: string;
  isAnalytic?: boolean;
  description?: string;
  active?: boolean;
  businessUnitId?: string;
  metadata?: Record<string, any>;
}

/**
 * Interface pour la réponse de création en lot
 */
export interface BatchCreateResponse {
  success: boolean;
  data: {
    created: number;
    errors?: Array<{ index: number; code?: string; message: string }>;
  };
}

/**
 * Interface pour la hiérarchie des comptes
 */
export interface AccountHierarchy extends Account {
  children?: AccountHierarchy[];
}

/**
 * Interface pour la recherche avancée de comptes
 */
export interface AccountSearchResponse {
  success: boolean;
  data: {
    results: Account[];
    total: number;
    query: string;
    filters: {
      type: string | null;
      standard: string | null;
      classCode: string | null;
      isAnalytic: boolean | null;
      isActive: boolean | null;
    };
  };
}

export const accountsApi = {
  /**
   * Récupérer tous les comptes
   * @param params.businessUnitId - ID de l'unité commerciale (optionnel)
   * @param params.businessUnitType - Type d'unité: 'company' | 'branch' | 'pos' (optionnel)
   * @description Les comptes peuvent être filtrés par unité commerciale pour voir
   * uniquement les comptes utilisés par une succursale ou point de vente spécifique
   */
  getAll: async (params?: AccountSearchParams): Promise<ApiResponse<PaginatedResponse<Account>>> => {
    const response = await ApiService.get<BackendAccountsResponse | PaginatedResponse<Account>>('/accounts', params);
    
    if (response.success && response.data) {
      // Vérifier si la réponse est au format backend (avec 'accounts') ou frontend (avec 'data')
      const rawData = response.data as any;
      
      if ('accounts' in rawData) {
        // Format backend: normaliser vers le format frontend
        return {
          success: true,
          data: normalizeAccountsResponse(rawData as BackendAccountsResponse)
        };
      }
      
      // Format frontend déjà normalisé ou structure attendue
      if ('data' in rawData && Array.isArray(rawData.data)) {
        return response as ApiResponse<PaginatedResponse<Account>>;
      }
      
      // Fallback: si c'est directement un tableau
      if (Array.isArray(rawData)) {
        return {
          success: true,
          data: {
            data: rawData,
            total: rawData.length,
            page: 1,
            pageSize: rawData.length,
            totalPages: 1
          }
        };
      }
    }
    
    return response as ApiResponse<PaginatedResponse<Account>>;
  },

  /**
   * Récupérer un compte par ID
   */
  getById: (id: string) => 
    ApiService.get<Account>(`/accounts/${id}`),

  /**
   * Récupérer un compte par son code comptable SYSCOHADA
   * @param code - Code comptable (ex: "411000")
   */
  getByCode: (code: string) => 
    ApiService.get<Account>(`/accounts/code/${code}`),

  /**
   * Récupérer la hiérarchie des comptes
   * @param rootId - ID du compte racine (optionnel, sinon retourne toute la hiérarchie)
   */
  getHierarchy: (rootId?: string) => 
    ApiService.get<AccountHierarchy[]>(`/accounts/hierarchy${rootId ? `/${rootId}` : ''}`),

  /**
   * Créer un nouveau compte
   * @param account - Données du compte à créer (code, name, type, companyId, fiscalYearId requis)
   * @param account.businessUnitId - ID de l'unité commerciale propriétaire (optionnel)
   */
  create: (account: CreateAccountRequest) => 
    ApiService.post<Account>('/accounts', account),

  /**
   * Mettre à jour un compte
   * Note: Le code ne peut pas être modifié après création
   */
  update: (id: string, account: UpdateAccountRequest) => 
    ApiService.put<Account>(`/accounts/${id}`, account),

  /**
   * Supprimer un compte
   * Note: Un compte avec des transactions ne peut pas être supprimé
   */
  delete: (id: string) => 
    ApiService.delete<void>(`/accounts/${id}`),

  /**
   * Créer plusieurs comptes en lot
   * @see POST /accounts/batch/create
   */
  createBatch: (accounts: CreateAccountRequest[]) => 
    ApiService.post<BatchCreateResponse>('/accounts/batch/create', accounts),

  /**
   * Importer des comptes depuis un fichier
   * @param file - Fichier CSV ou Excel
   * @param format - Format du fichier ('csv' ou 'excel')
   */
  importAccounts: (file: File, format: 'csv' | 'excel' = 'csv') => {
    const formData = new FormData();
    formData.append('file', file);
    return ApiService.post<{ success: boolean; data: { imported: number; errors?: string[] } }>(
      `/accounts/batch?action=import&format=${format}`,
      formData
    );
  },

  /**
   * Exporter les comptes
   * @param format - Format d'export ('csv' ou 'excel')
   * @param params - Filtres à appliquer
   */
  exportAccounts: (format: 'csv' | 'excel', params?: AccountSearchParams) => 
    ApiService.get<Blob>(`/accounts/batch?action=export&format=${format}`, params),

  /**
   * Rechercher des comptes avec filtres avancés
   * @see GET /accounts/search
   * 
   * @param query - Terme de recherche (code, nom ou description)
   * @param params - Paramètres de recherche supplémentaires
   */
  search: (query: string, params?: { 
    limit?: number;
    type?: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
    standard?: 'SYSCOHADA' | 'IFRS';
    classCode?: string;          // Classe SYSCOHADA (1-9)
    isAnalytic?: boolean;
    isActive?: boolean;
  } & BusinessUnitFilterBase) =>
    ApiService.get<AccountSearchResponse>(
      '/accounts/search', 
      { query, ...params }
    ),

  // ====== ACCOUNT INITIALIZATION ENDPOINTS ======

  /**
   * Récupérer la liste des plans comptables disponibles
   * @see GET /accounts/initialization/charts
   * 
   * Retourne les frameworks disponibles avec leur nombre de comptes:
   * - SYSCOHADA_NORMAL: 491 comptes
   * - SYSCOHADA_MINIMAL: 147 comptes
   * - IFRS: 225 comptes
   */
  getAvailableCharts: () =>
    ApiService.get<import('../../../types/account-initialization').ChartOfAccountsInfo[]>(
      '/accounts/initialization/charts'
    ),

  /**
   * Vérifier l'état d'initialisation d'un exercice fiscal
   * @see GET /accounts/initialization/status/:fiscalYearId
   * 
   * @param fiscalYearId - ID de l'exercice fiscal à vérifier
   */
  getInitializationStatus: (fiscalYearId: string) =>
    ApiService.get<import('../../../types/account-initialization').InitializationStatus>(
      `/accounts/initialization/status/${fiscalYearId}`
    ),

  /**
   * Initialiser les comptes pour un exercice fiscal
   * @see POST /accounts/initialization
   * 
   * Crée tous les comptes du plan comptable sélectionné pour l'exercice fiscal.
   * Si overwrite=true, supprime les comptes existants avant de créer.
   * 
   * @param data - Données d'initialisation (fiscalYearId, companyId, framework requis)
   */
  initializeAccounts: (data: import('../../../types/account-initialization').InitializeAccountsDto) =>
    ApiService.post<import('../../../types/account-initialization').InitializationResult>(
      '/accounts/initialization',
      data
    ),

  /**
   * Réinitialiser les comptes avec un nouveau framework
   * @see POST /accounts/initialization/reinitialize/:fiscalYearId
   * 
   * Supprime tous les comptes existants et réinitialise avec le nouveau framework.
   * ATTENTION: Cette opération est irréversible.
   * 
   * @param fiscalYearId - ID de l'exercice fiscal
   * @param data - Données de réinitialisation (companyId, framework requis)
   */
  reinitializeAccounts: (fiscalYearId: string, data: import('../../../types/account-initialization').ReinitializeAccountsDto) =>
    ApiService.post<import('../../../types/account-initialization').InitializationResult>(
      `/accounts/initialization/reinitialize/${fiscalYearId}`,
      data
    )
};